import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { DualListSelectorControl } from '@patternfly/react-core';
import SimpleTable, { RowSelection } from '../SimpleTable/SimpleTable';

import { AngleDoubleLeftIcon, AngleDoubleRightIcon, AngleLeftIcon, AngleRightIcon } from '@patternfly/react-icons';
import './PrimaryDualList.css';
import { sortLocale } from '../../../utils/string';
import { isItemInCollection } from '../utils/collection';

const PrimaryDualList = ({
  primaryItems,
  secondaryItems,
  onPrimarySelected,
  selectedItems,
  onSelectionChanged,
  sortProperty,
  idIteratee: _idIteratee = _.identity,
  columns,
  emptyListText,
  isLoadingModels,
  isSingleSelect
}) => {
  const idIteratee = useMemo(() => _.iteratee(_idIteratee), [_idIteratee]);
  const [primary, setPrimary] = useState(null);
  const [availableSignals, setAvailableSignals] = useState(secondaryItems);

  const originalAvailableSignals = useRef(availableSignals);
  const sortedSelectedItems = useMemo(() => sortLocale(selectedItems, sortProperty),
    [selectedItems, sortProperty]);

  const _onPrimarySelected = useCallback(items => {
    setPrimary(() => _.head(items));
    onPrimarySelected(_.head(items));
  }, [onPrimarySelected]);

  useEffect(() => {
    originalAvailableSignals.current = secondaryItems;
    setAvailableSignals(() => originalAvailableSignals.current);
  }, [secondaryItems, sortProperty]);

  const _setSelectedItems = useCallback(items => {
    const newItems = sortLocale(items, sortProperty);
    onSelectionChanged?.(newItems);
  }, [onSelectionChanged, sortProperty]);

  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedSelected, setSelectedSelected] = useState([]);

  const addSelected = useCallback(() => {
    if (isSingleSelect) {
      _setSelectedItems([...selectedAvailable]);
    } else {
      _setSelectedItems([...selectedItems, ...selectedAvailable]);
    }

    setAvailableSignals(_.reject(availableSignals, isItemInCollection(selectedAvailable, idIteratee)));
    setSelectedAvailable([]);
  }, [_setSelectedItems, availableSignals, idIteratee, isSingleSelect, selectedAvailable, selectedItems]);

  const addAllVisible = useCallback(() => {
    _setSelectedItems([...selectedItems, ...availableSignals]);
    setAvailableSignals([]);
    setSelectedAvailable([]);
  }, [_setSelectedItems, availableSignals, selectedItems]);

  const _readdItemsToAvailable = useCallback((removeThese) => {
    const readdToAvailable = _.filter(removeThese, isItemInCollection(originalAvailableSignals.current, idIteratee));
    setAvailableSignals([...availableSignals, ...readdToAvailable]);
  }, [availableSignals, idIteratee]);

  const removeAllVisible = useCallback(() => {
    _readdItemsToAvailable(selectedItems);
    _setSelectedItems([]);
    setSelectedSelected([]);
  }, [_readdItemsToAvailable, _setSelectedItems, selectedItems]);

  const removeSelected = useCallback(() => {
    _readdItemsToAvailable(selectedSelected);
    _setSelectedItems(_.reject(selectedItems, isItemInCollection(selectedSelected, idIteratee)));
    setSelectedSelected([]);
  }, [_readdItemsToAvailable, _setSelectedItems, idIteratee, selectedItems, selectedSelected]);

  const primarySelectedRows = useMemo(() => [primary], [primary]);
  const sortedAvailableSignals = useMemo(() => sortLocale(availableSignals, sortProperty),
    [availableSignals, sortProperty]);

  return (
    <div className={'primaryDualContainer'}>
      <div className={'primaryList'}>
        <SimpleTable
          isLoading={isLoadingModels}
          compact
          columns={columns[0]}
          data={primaryItems}
          selectedRows={primarySelectedRows}
          rowSelection={RowSelection.SINGLE}
          onSelectedRows={_onPrimarySelected}
          emptyListText={_.get(emptyListText, 0, undefined)}
        />
      </div>
      <div className={'secondList'}>
        <SimpleTable
          compact
          rowSelection={isSingleSelect ? RowSelection.SINGLE : RowSelection.MULTI}
          columns={columns[1]}
          selectedRows={selectedAvailable}
          onSelectedRows={setSelectedAvailable}
          data={sortedAvailableSignals}
          emptyListText={_.get(emptyListText, 1, undefined)}
        />
      </div>
      <div className={'controlList'}>
        <DualListSelectorControl isDisabled={_.isEmpty(selectedAvailable)} onClick={addSelected} >
          <AngleRightIcon />
        </DualListSelectorControl>
        { !isSingleSelect && (<>
          <DualListSelectorControl isDisabled={_.isEmpty(availableSignals)} onClick={addAllVisible}>
            <AngleDoubleRightIcon />
          </DualListSelectorControl>
          <DualListSelectorControl isDisabled={_.isEmpty(selectedItems)} onClick={removeAllVisible}>
            <AngleDoubleLeftIcon />
          </DualListSelectorControl>
        </>) }
        <DualListSelectorControl onClick={removeSelected} isDisabled={_.isEmpty(selectedSelected)} >
          <AngleLeftIcon />
        </DualListSelectorControl>
      </div>

      <div className={'thirdList'}>
        <SimpleTable
          compact
          rowSelection={RowSelection.MULTI}
          columns={columns[2]}
          selectedRows={selectedSelected}
          onSelectedRows={setSelectedSelected}
          data={sortedSelectedItems}
          emptyListText={_.get(emptyListText, 2, undefined)}
        />
      </div>
    </div>
  );
};

PrimaryDualList.propTypes = {
  primaryItems: PropTypes.array,
  secondaryItems: PropTypes.array,
  onPrimarySelected: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  sortProperty: PropTypes.string,
  idIteratee: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  columns: PropTypes.array.isRequired,
  emptyListText: PropTypes.arrayOf(PropTypes.string),
  isSingleSelect: PropTypes.bool
};

export default React.memo(PrimaryDualList);
